// Used on Study Overview tab, Activities page, and Request Workflow tab
import { useState, useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import PropTypes from "prop-types"
import { PlusOutlined } from "@ant-design/icons"
import PrimeButton from "components-design-system/PrimeButton/PrimeButton"

// components
import { Panel } from "components-design-system"
import TeamModal from "./TeamModal/MyTeamModal"
import RefreshTeamConfirmationModal from "./RefreshTeamConfirmationModal/RefreshTeamConfirmationModal"

// api
import { useRefreshActivityTeam } from "api/hooks/activities/useActivityTeamApi"
import { useUserPermissions } from "api/hooks"

// assets
import empty_team_icon from "assets/images/empty_team.png"

// styles
import "./MyTeamPanel.scss"

const Team = ({
  type = "",
  postTeam = () => {},
  team = [],
  roles = [],
  title = "",
  panelClassName = "",
  style = {},
}) => {
  const { studyId, activityId } = useParams()
  const { data: permissions } = useUserPermissions()

  const refreshActivityTeam = useRefreshActivityTeam(studyId, activityId)

  const [selectedRole, setSelectedRole] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showTeamModal, setShowTeamModal] = useState(false)

  useEffect(() => {
    if (showTeamModal === false) setSelectedRole(null)
  }, [showTeamModal])

  const sortedTeam = useMemo(() => {
    if (Array.isArray(team)) {
      return team
        ?.sort((a, b) => (a.role_name.toLowerCase() > b.role_name.toLowerCase() ? 1 : -1))
        ?.filter((team) => team?.members?.length > 0)
    }

    return []
  }, [team])

  const hasPermissions = useMemo(() => {
    switch (type) {
      case "study":
        return permissions?.["study.edit"]
      case "activity":
        return permissions?.["workflow.edit"] === true
      case "request":
        return permissions?.["study.edit"] === true // TODO: UPDATE LATER IF NEEDED
      default:
        return []
    }
  }, [permissions, type])

  const actions = useMemo(() => {
    let buttons = []
    if (hasPermissions) {
      if (type === "activity") {
        buttons.push(
          <PrimeButton
            key="refresh-team-btn"
            icon={<i className="fal fa-redo" />}
            tooltip={"Any changes that were made to the team at the study level will be reflected on this activity"}
            onClick={() => setShowConfirmationModal(true)}
            style={{ paddingTop: 9.5, paddingBottom: 6.5 }}
          />
        )
      }
      buttons.push(
        <PrimeButton
          key="add-team-member-btn"
          icon={<PlusOutlined />}
          onClick={() => setShowTeamModal(true)}
        >
          Add team
        </PrimeButton>
      )
    }

    return buttons
  }, [hasPermissions, type])

  const onPostTeam = async (payload) => {
    return postTeam({
      body: payload,
    })
  }

  const onRefreshTeam = () => {
    refreshActivityTeam(
      {},
      {
        onSuccess: () => {
          setShowConfirmationModal(false)
        },
      }
    )
  }

  const handleOpenRole = (role) => {
    setSelectedRole(role)
    setShowTeamModal((state) => !state)
  }

  const renderEmptyTeam = (
    <div className="empty-team">
      <img
        className="empty_team_icon"
        src={empty_team_icon}
        alt="empty team container"
      />
      <p className="empty-team-label">Add your team members</p>
    </div>
  )

  const renderTeam = (
    <div className="team-roles-list-container">
      {sortedTeam.map((team) => {
        return (
          <div
            className="team-role-container"
            key={team.role_name}
          >
            <div className="team-role-header-container">
              <div className="role-title">{team.role_name}</div>
              <i
                className="fal fa-edit"
                onClick={() => handleOpenRole(team)}
              />
            </div>
            <div className="role-member-list">
              {team.members
                .sort((a, b) => (a.person_fullname.toLowerCase() > b.person_fullname.toLowerCase() ? 1 : -1))
                .map((member) => (
                  <p
                    key={`${member.person_id}-${team.role_id}`}
                    className="team-role-member"
                  >
                    {member.person_fullname}
                  </p>
                ))}
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <Panel
      title="MY TEAM"
      iconClassName="fal fa-user-friends"
      actions={actions}
      className={`panel-col team-panel ${panelClassName}`}
      style={style}
    >
      <div className="team-container">{team.length > 0 ? renderTeam : renderEmptyTeam}</div>
      {hasPermissions && showTeamModal && (
        <TeamModal
          show={showTeamModal}
          setShow={setShowTeamModal}
          selectedRole={selectedRole}
          team={team}
          roles={roles}
          onPostTeam={onPostTeam}
        />
      )}
      {hasPermissions === true && type === "activity" && (
        <RefreshTeamConfirmationModal
          subheader={title}
          handleSaveChanges={onRefreshTeam}
          showModal={showConfirmationModal}
          onHide={() => setShowConfirmationModal(false)}
        />
      )}
    </Panel>
  )
}

Team.propTypes = {
  /**
   * Determines if the team is for a study, activity, or request.
   */
  type: PropTypes.oneOf("study", "activity", "request"),
  /**
   * API call to post team
   */
  postTeam: PropTypes.func,
  team: PropTypes.array,
  /**
   * "study/activity" vs "request" roles
   */
  roles: PropTypes.array,
  /**
   * (sub)title for the Confirm Team Refresh modal
   */
  title: PropTypes.string,
  panelClassName: PropTypes.string,
  style: PropTypes.object,
}

export default Team
